/* Formulario de una Solicitud a SST, armado con el catálogo que declara EMSYS
 * (tipo de solicitud -> campos). Se usa para cargar una nueva y para corregir
 * una devuelta; el servidor vuelve a validar con el mismo catálogo.
 */
import {
  IonButton,
  IonDatetime,
  IonIcon,
  IonInput,
  IonSelect,
  IonSelectOption,
  IonSpinner,
  IonTextarea,
} from "@ionic/react";
import { closeCircleOutline } from "ionicons/icons";
import { useEffect, useState } from "react";

import { BuscadorRemoto, campoEstilo, Etiqueta, isoHoyMas } from "src/components/SolicitudCapacitacionCampos";
import {
  CampoSolicitud,
  ColaboradorBusqueda,
  PsBusqueda,
  SolicitudSstAPI,
  TipoSolicitudSst,
} from "src/services/sst";

const bordeInput: React.CSSProperties = { border: "1px solid #ccc", borderRadius: 4, paddingLeft: 8 };
const bordeTextarea: React.CSSProperties = { border: "1px solid #ccc", borderRadius: 4, padding: "0 8px" };

export interface ValoresIniciales {
  valores: Record<string, any>;
  ps?: PsBusqueda | null;
}

interface Props {
  tipo: TipoSolicitudSst;
  inicial?: ValoresIniciales;
  textoBoton: string;
  /** Mientras corre, el formulario queda deshabilitado. Devuelve true si salió
   * bien y la pantalla navega a otra (entonces no se vuelve a habilitar). */
  onEnviar: (payload: Record<string, any>) => Promise<boolean>;
  onError: (mensaje: string) => void;
}

// IonDatetime devuelve un ISO completo ("2026-09-28T09:30:00.000-03:00"); el
// backend espera YYYY-MM-DD y HH:mm.
const soloFecha = (v?: string | null) => (v ? String(v).substring(0, 10) : "");
const soloHora = (v?: string | null) => {
  if (!v) return "";
  const texto = String(v);
  return texto.includes("T") ? texto.substring(11, 16) : texto.substring(0, 5);
};

const vacio = (v: any) => v === undefined || v === null || (typeof v === "string" && !v.trim()) || (Array.isArray(v) && !v.length);

const SolicitudSstFormulario: React.FC<Props> = ({ tipo, inicial, textoBoton, onEnviar, onError }) => {
  const [valores, setValores] = useState<Record<string, any>>({});
  const [ps, setPs] = useState<PsBusqueda | null>(null);
  const [colaboradores, setColaboradores] = useState<ColaboradorBusqueda[]>([]);
  const [enviando, setEnviando] = useState(false);

  // Al cambiar de tipo (o al abrir una devuelta) se reinicia el formulario.
  useEffect(() => {
    const base: Record<string, any> = {};
    tipo.campos.forEach((c) => {
      if (c.tipo !== "PS" && c.tipo !== "COLABORADORES") base[c.codigo] = inicial?.valores?.[c.codigo] ?? "";
    });
    setValores(base);
    setPs(inicial?.ps ?? null);
    setColaboradores(inicial?.valores?.colaboradores ?? []);
  }, [tipo, inicial]);

  const hoy = isoHoyMas(0);
  const set = (codigo: string, valor: any) => setValores((prev) => ({ ...prev, [codigo]: valor }));

  const validar = (): string | null => {
    for (const c of tipo.campos) {
      const valor = c.tipo === "PS" ? ps : c.tipo === "COLABORADORES" ? colaboradores : valores[c.codigo];
      if (c.obligatorio && vacio(valor)) {
        if (c.tipo === "PS") return "Seleccione el punto de servicio";
        if (c.tipo === "COLABORADORES") return "Agregue al menos un colaborador involucrado";
        return `Complete "${c.etiqueta}"`;
      }
      if (c.tipo === "FECHA" && valor) {
        if (tipo.regla_fecha === "pasada" && valor > hoy) return `"${c.etiqueta}" no puede ser futura`;
        if (tipo.regla_fecha === "futura" && valor < hoy) return `"${c.etiqueta}" no puede ser anterior a hoy`;
      }
    }
    return null;
  };

  const enviar = async () => {
    const error = validar();
    if (error) {
      onError(error);
      return;
    }
    const payload: Record<string, any> = { tipo: tipo.codigo };
    tipo.campos.forEach((c) => {
      if (c.tipo === "PS") payload[c.codigo] = ps?.id;
      else if (c.tipo === "COLABORADORES") payload[c.codigo] = colaboradores.map((o) => o.legajo);
      else payload[c.codigo] = typeof valores[c.codigo] === "string" ? valores[c.codigo].trim() : valores[c.codigo];
    });
    setEnviando(true);
    let ok = false;
    try {
      ok = await onEnviar(payload);
    } finally {
      if (!ok) setEnviando(false);
    }
  };

  const campo = (c: CampoSolicitud) => {
    const etiqueta = <Etiqueta texto={c.etiqueta} obligatorio={c.obligatorio} />;
    switch (c.tipo) {
      case "PS":
        return (
          <div key={c.codigo} style={campoEstilo}>
            {etiqueta}
            <BuscadorRemoto<PsBusqueda>
              etiqueta="Buscar por nombre o código"
              valor={ps}
              onChange={setPs}
              buscar={async (q) => (await SolicitudSstAPI.puntosServicio(q)).data.data || []}
              getLabel={(o) => `${o.codigo} - ${o.nombre}`}
              getId={(o) => o.id}
              disabled={enviando}
            />
          </div>
        );
      case "COLABORADORES":
        return (
          <div key={c.codigo} style={campoEstilo}>
            {etiqueta}
            <BuscadorRemoto<ColaboradorBusqueda>
              etiqueta="Agregar por legajo o nombre"
              valor={null}
              onChange={(o) => {
                if (o && !colaboradores.some((x) => x.legajo === o.legajo)) setColaboradores([...colaboradores, o]);
              }}
              buscar={async (q) => (await SolicitudSstAPI.colaboradores(q)).data.data || []}
              getLabel={(o) => `${o.legajo} - ${o.nombre}${o.cargo ? ` (${o.cargo})` : ""}`}
              getId={(o) => o.legajo}
              minimo={1}
              limpiarAlElegir
              disabled={enviando}
            />
            {colaboradores.length > 0 && (
              <div style={{ border: "1px solid #ddd", borderRadius: 6, marginTop: 8 }}>
                {colaboradores.map((o, i) => (
                  <div
                    key={o.legajo}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      padding: "4px 8px",
                      borderTop: i ? "1px solid #eee" : "none",
                      fontSize: 14,
                    }}
                  >
                    <span style={{ flex: 1 }}>
                      {o.legajo} - {o.nombre}
                    </span>
                    <IonButton
                      fill="clear"
                      size="small"
                      color="danger"
                      disabled={enviando}
                      onClick={() => setColaboradores(colaboradores.filter((x) => x.legajo !== o.legajo))}
                    >
                      <IonIcon icon={closeCircleOutline} slot="icon-only" />
                    </IonButton>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      case "FECHA":
        return (
          <div key={c.codigo} style={{ ...campoEstilo, flex: "1 1 200px" }}>
            {etiqueta}
            {/* IonDatetime (rueda de Ionic) como el resto de la app: el
                calendario nativo de type="date" sale oscuro y desbordado en
                movil. Sin max explicito solo deja elegir el anio actual. */}
            <IonDatetime
              displayFormat="DD/MM/YYYY"
              pickerFormat="DD/MM/YYYY"
              cancelText="Cancelar"
              doneText="Listo"
              placeholder="dd/mm/aaaa"
              min={tipo.regla_fecha === "futura" ? hoy : isoHoyMas(-3650)}
              max={tipo.regla_fecha === "pasada" ? hoy : isoHoyMas(730)}
              value={valores[c.codigo] || undefined}
              onIonChange={(e) => set(c.codigo, soloFecha(e.detail.value))}
              style={{ ...bordeInput, padding: "10px 8px" }}
              disabled={enviando}
            />
          </div>
        );
      case "HORA":
        return (
          <div key={c.codigo} style={{ ...campoEstilo, flex: "1 1 200px" }}>
            {etiqueta}
            <IonDatetime
              displayFormat="HH:mm"
              pickerFormat="HH:mm"
              cancelText="Cancelar"
              doneText="Listo"
              placeholder="hh:mm"
              value={valores[c.codigo] || undefined}
              onIonChange={(e) => set(c.codigo, soloHora(e.detail.value))}
              style={{ ...bordeInput, padding: "10px 8px" }}
              disabled={enviando}
            />
          </div>
        );
      case "OPCION":
        return (
          <div key={c.codigo} style={campoEstilo}>
            {etiqueta}
            <IonSelect
              interface="action-sheet"
              interfaceOptions={{ header: c.etiqueta }}
              cancelText="Cancelar"
              placeholder="Seleccionar"
              value={valores[c.codigo] || undefined}
              onIonChange={(e) => set(c.codigo, e.detail.value)}
              style={bordeInput}
              disabled={enviando}
            >
              {c.opciones.map((o) => (
                <IonSelectOption key={o.valor} value={o.valor}>
                  {o.etiqueta}
                </IonSelectOption>
              ))}
            </IonSelect>
          </div>
        );
      case "TEXTAREA":
        return (
          <div key={c.codigo} style={campoEstilo}>
            {etiqueta}
            <IonTextarea
              value={valores[c.codigo]}
              onIonChange={(e) => set(c.codigo, e.detail.value || "")}
              autoGrow
              rows={3}
              maxlength={4000}
              style={bordeTextarea}
              disabled={enviando}
            />
          </div>
        );
      default:
        return (
          <div key={c.codigo} style={campoEstilo}>
            {etiqueta}
            <IonInput
              value={valores[c.codigo]}
              maxlength={c.max || 300}
              onIonChange={(e) => set(c.codigo, e.detail.value || "")}
              style={bordeInput}
              disabled={enviando}
            />
          </div>
        );
    }
  };

  // Fecha y hora van en la misma fila, como en el formulario original.
  const bloques: JSX.Element[] = [];
  for (let i = 0; i < tipo.campos.length; i++) {
    const c = tipo.campos[i];
    const siguiente = tipo.campos[i + 1];
    if (c.tipo === "FECHA" && siguiente?.tipo === "HORA") {
      bloques.push(
        <div key={`${c.codigo}-fila`} style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
          {campo(c)}
          {campo(siguiente)}
        </div>
      );
      i++;
    } else {
      bloques.push(campo(c));
    }
  }

  return (
    <>
      {bloques}
      <IonButton expand="block" disabled={enviando} onClick={enviar}>
        {enviando ? <IonSpinner name="crescent" /> : textoBoton}
      </IonButton>
    </>
  );
};

export default SolicitudSstFormulario;
